import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Settings, User, Users, Search, Radio, Video, RefreshCw, Calendar, Grid3X3, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function XtreamDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [lastUpdated, setLastUpdated] = useState({
    liveTV: new Date(),
    movies: new Date(Date.now() - 7 * 60000),
    series: new Date(Date.now() - 9 * 60000)
  });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: '2-digit',
      year: 'numeric'
    });
  };

  const formatExpiration = (dateStr) => {
    if (!dateStr || dateStr === 'lifetime') return 'January 18, 2038';
    // Xtream panels return exp_date as a Unix timestamp string (seconds)
    const num = Number(dateStr);
    const date = !isNaN(num) && num > 1e8 ? new Date(num * 1000) : new Date(dateStr);
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return `${seconds} sec ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes} mins ago`;
  };

  const handleRefresh = (category, e) => {
    e.stopPropagation();
    setLastUpdated(prev => ({
      ...prev,
      [category]: new Date()
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleSwitchUser = () => {
    logout();
    navigate('/login');
  };

  return (
    <div 
      className="xtream-dashboard" 
      data-testid="xtream-dashboard"
      style={{backgroundImage: `url(${process.env.PUBLIC_URL}/app-background.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat'}}
    >

      {/* Header Bar */}
      <header className="xtream-header" data-testid="xtream-header">
        <div className="header-left">
          <div className="xtream-logo" data-testid="xtream-logo">
            <img src="/logo.png" alt="FTA Xtream" style={{height:'40px', width:'auto', objectFit:'contain'}} />
          </div>
          <div className="header-datetime">
            <span className="header-time">{formatTime(currentTime)}</span>
            <span className="header-date">{formatDate(currentTime)}</span>
          </div>
        </div>

        <div className="header-center">
          <button 
            className="header-search-btn"
            onClick={() => navigate('/channels?search=')}
            data-testid="master-search-btn"
          >
            <Search className="w-5 h-5" />
            <span>Master Search</span>
          </button>
        </div>

        <div className="header-right">
          <button className="header-icon-btn" title="Radio">
            <Radio className="w-5 h-5" />
          </button>
          <button 
            className="header-icon-btn"
            onClick={() => setShowUserInfo(true)}
            title="User Profile"
            data-testid="user-profile-btn"
          >
            <User className="w-5 h-5" />
          </button>
          <button className="header-icon-btn rec-btn" title="Recording">
            <span className="rec-dot"></span>
            <span className="rec-text">REC</span>
          </button>
          <button className="header-icon-btn" title="Camera">
            <Video className="w-5 h-5" />
          </button>
          <button 
            className="header-icon-btn"
            onClick={() => navigate('/settings')}
            title="Settings"
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button 
            className="header-icon-btn"
            onClick={handleSwitchUser}
            title="Switch User"
            data-testid="switch-user-btn"
          >
            <Users className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Quick Access Bar */}
      <div className="quick-access-bar" data-testid="quick-access-bar">
        <button 
          className="quick-access-btn"
          onClick={() => navigate('/epg')}
          data-testid="live-epg-btn"
        >
          <Calendar className="w-5 h-5" />
          <span>LIVE WITH EPG</span>
        </button>
        <button 
          className="quick-access-btn"
          onClick={() => navigate('/categories')}
          data-testid="multi-screen-btn"
        >
          <Grid3X3 className="w-5 h-5" />
          <span>MULTI-SCREEN</span>
        </button>
        <button 
          className="quick-access-btn"
          onClick={() => navigate('/history')}
          data-testid="catch-up-btn"
        >
          <CheckCircle className="w-5 h-5" />
          <span>CATCH UP</span>
        </button>
      </div>

      {/* Main Content - 3D Pedestals */}
      <div className="xtream-main-content" data-testid="main-content">
        
        {/* Live TV Card */}
        <div 
          className="content-card-3d"
          onClick={() => handleNavigation('/live')}
          data-testid="live-tv-card"
        >
          <div className="card-illustration">
            <img src="/live-tv.png" alt="Live TV" />
          </div>
          <div className="card-meta">
            <span className="last-updated">Last updated: {getTimeAgo(lastUpdated.liveTV)}</span>
            <button className="refresh-btn" onClick={(e) => handleRefresh('liveTV', e)}>
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Movies Card */}
        <div 
          className="content-card-3d"
          onClick={() => handleNavigation('/movies')}
          data-testid="movies-card"
        >
          <div className="card-illustration">
            <img src="/movies.png" alt="Movies" />
          </div>
          <div className="card-meta">
            <span className="last-updated">Last updated: {getTimeAgo(lastUpdated.movies)}</span>
            <button className="refresh-btn" onClick={(e) => handleRefresh('movies', e)}>
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Series Card */}
        <div 
          className="content-card-3d"
          onClick={() => handleNavigation('/series')}
          data-testid="series-card"
        >
          <div className="card-illustration">
            <img src="/series.png" alt="Series" />
          </div>
          <div className="card-meta">
            <span className="last-updated">Last updated: {getTimeAgo(lastUpdated.series)}</span>
            <button className="refresh-btn" onClick={(e) => handleRefresh('series', e)}>
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer Bar */}
      <footer className="xtream-footer" data-testid="xtream-footer">
        <div className="footer-left">
          <span className="expiration-label">Expiration :</span>
          <span className="expiration-date">
            {user?.subscription_type === 'lifetime' 
              ? 'January 18, 2038' 
              : formatExpiration(user?.subscription_expires)
            }
          </span>
        </div>
        <div className="footer-right">
          <span className="logged-in-label">Logged in :</span>
          <span className="logged-in-user">{user?.username || 'MAIN PC'}</span>
        </div>
      </footer>

      {/* User Info Modal */}
      <Dialog open={showUserInfo} onOpenChange={setShowUserInfo}>
        <DialogContent className="xtream-modal" data-testid="user-info-modal">
          <DialogHeader>
            <DialogTitle className="modal-title">
              <User className="w-6 h-6" />
              Subscription Info
            </DialogTitle>
          </DialogHeader>
          <div className="user-info-content">
            <div className="info-row">
              <span className="info-label">Username</span>
              <span className="info-value">{user?.username}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Email</span>
              <span className="info-value">{user?.email}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Subscription</span>
              <span className="info-value subscription-type">
                {user?.subscription_type === 'lifetime' ? 'LIFETIME' : 
                 user?.subscription_type?.toUpperCase() || 'NONE'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Expires</span>
              <span className="info-value">
                {user?.subscription_type === 'lifetime' 
                  ? 'January 18, 2038' 
                  : formatExpiration(user?.subscription_expires)
                }
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Credits</span>
              <span className="info-value credits">{user?.credits?.toFixed(1) || '0.0'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Max Devices</span>
              <span className="info-value">{user?.max_devices || 1}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Account Status</span>
              <span className={`info-value status ${user?.is_active ? 'active' : 'inactive'}`}>
                {user?.is_active ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
